import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserStore } from '../store/userStore'
import { useLanguageStore } from '../store/languageStore'
import type { LanguageCode } from '../services/languageApi.js'
import { FrogMascot } from '../components/FrogMascot'
import { SignUpPromptModal } from '../components/common/SignUpPromptModal'
import { GuestLanguageModal } from '../components/common/GuestLanguageModal'
import { logGuestStart } from '../services/api'
import { storeGuestUserId } from '../utils/guestStorage'
import { useScrollAnimation } from '../hooks/useScrollAnimation'

// Helper to calculate level progress
const calculateLevelProgress = (totalXP: number) => {
  // Base XP for level 1
  const BASE_XP = 100
  // Growth factor
  const GROWTH_FACTOR = 1.5

  // Calculate level based on XP
  let level = 1
  let xpForNextLevel = BASE_XP

  while (totalXP >= xpForNextLevel) {
    level++
    xpForNextLevel = Math.floor(BASE_XP * Math.pow(GROWTH_FACTOR, level - 1))
  }

  // Calculate XP required for current level
  const xpRequiredForLevel = (lvl: number) => {
    if (lvl === 1) return 100
    return Math.floor(BASE_XP * Math.pow(GROWTH_FACTOR, lvl - 1))
  }

  // XP required to complete the PREVIOUS level
  const xpForPreviousLevel = level > 1 ? xpRequiredForLevel(level - 1) : 0
  // XP required to complete the CURRENT level
  const xpForCurrentLevel = xpRequiredForLevel(level)
  // How much XP we have within this level
  const xpInCurrentLevel = totalXP - xpForPreviousLevel
  // How much XP is needed to complete this level
  const xpNeededInLevel = xpForCurrentLevel - xpForPreviousLevel
  const percentageToNextLevel = Math.min(100, Math.max(0, Math.round((xpInCurrentLevel / xpNeededInLevel) * 100)))

  return {
    currentLevel: level,
    xpInCurrentLevel,
    xpForCurrentLevel: xpForPreviousLevel,
    xpForNextLevel: xpForCurrentLevel,
    xpNeededInLevel,
    percentageToNextLevel,
  }
}

export function Home() {
  const navigate = useNavigate()
  const { user, setUser } = useUserStore()
  const currentLanguage = useLanguageStore((state) => state.currentLanguage)
  const availableLanguages = useLanguageStore((state) => state.availableLanguages)
  const setCurrentLanguage = useLanguageStore((state) => state.setCurrentLanguage)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false)
  const [showLanguageModal, setShowLanguageModal] = useState(false)
  const [lockedFeature, setLockedFeature] = useState('')

  const targetLanguage = useMemo(() => {
    const match = availableLanguages.find((lang) => lang.code === currentLanguage)
    return {
      code: currentLanguage || 'ja',
      name: match?.name || (currentLanguage ? currentLanguage.toUpperCase() : 'Japanese'),
      nativeName: match?.nativeName || match?.name || 'Japanese',
    }
  }, [availableLanguages, currentLanguage])

  const heroText = useMemo(() => {
    if (!user) {
      return {
        hero: 'Speak a new language confidently',
        practice: 'Practice with AI, get instant feedback, and learn faster.',
      }
    }

    const headings: Record<string, { hero: string; practice: string }> = {
      ja: { hero: '日本語を話しましょう', practice: 'Practice Conversational Japanese with AI' },
      es: { hero: '¡Aprendamos español!', practice: 'Practica conversaciones en español con IA' },
      zh: { hero: '让我们学中文！', practice: '用AI练习中文会话' },
      tl: { hero: 'Mag-aral tayo ng Tagalog!', practice: 'Magsanay ng Tagalog na pag-uusap gamit ang AI' },
      fr: { hero: 'Apprenons le français !', practice: 'Pratique des conversations en français avec l\'IA' },
      it: { hero: 'Impariamo l\'italiano!', practice: 'Pratica conversazioni in italiano con l\'IA' },
      de: { hero: 'Lass uns Deutsch lernen!', practice: 'Übe deutsche Gespräche mit KI' },
      pt: { hero: 'Vamos aprender português!', practice: 'Pratique conversação em português com IA' },
      hi: { hero: 'आइए हिन्दी सीखें!', practice: 'AI के साथ हिन्दी बातचीत का अभ्यास करें' },
      to: { hero: 'Tau ako \'o e lea Tonga!', practice: 'Fai ha talanoa Tonga mo e AI' },
      sm: { hero: 'Tatou a\'oa\'o le Samoa!', practice: 'Fa\'ata\'ita\'i talanoaga Samoa ma le AI' },
    }

    const chosen = headings[targetLanguage.code] || headings[targetLanguage.code.toLowerCase()]
    const fallbackHero = `Let's learn ${targetLanguage.name}!`
    const fallbackPractice = `Practice conversational ${targetLanguage.name} with AI`

    return {
      hero: chosen?.hero || fallbackHero,
      practice: chosen?.practice || fallbackPractice,
    }
  }, [targetLanguage, user])

  // Calculate level progress
  const levelProgress = user ? calculateLevelProgress(user.totalXP) : null

  // Handle navigation with authentication check
  const handleNavigate = (path: string, featureName?: string) => {
    if (!user && featureName) {
      setLockedFeature(featureName)
      setShowSignUpPrompt(true)
    } else {
      navigate(path)
    }
  }

  const handleLanguageSelect = async (langCode: LanguageCode) => {
    setCurrentLanguage(langCode)

    let guestUserId: string | undefined
    if (!user) {
      try {
        const landingPagePath =
          typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/'
        const referrer = typeof document !== 'undefined' ? document.referrer || undefined : undefined
        const response = await logGuestStart({
          languageCode: langCode,
          landingPagePath,
          referrer,
        })
        guestUserId = response?.guestUserId
        if (guestUserId) {
          storeGuestUserId(guestUserId)
        }
      } catch (error) {
        console.error('Failed to register guest visitor', error)
      }
    }

    setShowLanguageModal(false)
    navigate('/conversation', { state: { selectedLanguage: langCode, guestUserId } })
  }

  // Scroll animation hooks for different sections
  const heroAnimation = useScrollAnimation<HTMLDivElement>({ threshold: 0.1 })
  const cardsAnimation = useScrollAnimation<HTMLDivElement>({ threshold: 0.1, delay: 100 })
  const sellingPointsAnimation = useScrollAnimation<HTMLDivElement>({ threshold: 0.1 })

  return (
    <>
      {/* Modals - rendered outside page-shell for proper viewport positioning */}
      <SignUpPromptModal
        isOpen={showSignUpPrompt}
        onClose={() => setShowSignUpPrompt(false)}
        featureName={lockedFeature}
      />

      <GuestLanguageModal
        isOpen={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
        onSelectLanguage={handleLanguageSelect}
      />

      <div className="page-shell min-h-screen">
        {/* Main container with breathing room */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
          {/* Top Bar */}
          <div className="flex justify-end mb-6 md:mb-8">
            {!user ? (
              <button
                onClick={() => navigate('/auth')}
                className="text-primary-600 hover:text-primary-500 text-sm font-medium transition-colors duration-200"
              >
                Sign In / Sign Up
              </button>
            ) : (
              <div className="flex items-center space-x-3 md:space-x-4">
                <span className="text-sm text-neutral-600 dark:text-neutral-400 truncate max-w-[140px] md:max-w-none">
                  Welcome, {user.username}
                </span>
                <button
                  onClick={() => navigate('/profile')}
                  className="flex items-center space-x-1.5 text-primary-600 hover:text-primary-500 text-sm font-medium transition-colors duration-200"
                >
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M12 2a5 5 0 0 0-5 5c0 2.761 2.239 5 5 5s5-2.239 5-5a5 5 0 0 0-5-5zm0 14c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5z" />
                  </svg>
                  <span>Profile</span>
                </button>
              </div>
            )}
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Hero Section with scroll animation */}
            <div
              ref={heroAnimation.ref}
              className={`text-center mb-12 md:mb-16 relative pt-12 md:pt-20 ${heroAnimation.animationClasses}`}
            >
              <FrogMascot />
              {/* Updated typography: font-semibold instead of font-extrabold for softer feel */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6 md:mb-8 leading-tight tracking-tight">
                {heroText.hero}
              </h1>
              <p className="text-xl sm:text-2xl md:text-3xl text-neutral-600 dark:text-neutral-300 mb-10 max-w-2xl mx-auto leading-relaxed">
                {heroText.practice}
              </p>

              {!user && (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                  {/* Primary CTA with soft gradient */}
                  <button
                    onClick={() => setShowLanguageModal(true)}
                    className="w-full sm:w-auto px-8 py-4 bg-gradient-to-br from-primary-500 to-primary-600 hover:from-primary-400 hover:to-primary-500 text-white text-lg font-semibold rounded-xl shadow-md hover:shadow-lg hover:shadow-primary-500/25 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0"
                  >
                    Start Talking Now - Free
                  </button>
                  {/* Secondary CTA with warm neutral */}
                  <button
                    onClick={() => navigate('/auth', { state: { mode: 'signup' } })}
                    className="w-full sm:w-auto px-8 py-4 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700 text-lg font-medium rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                  >
                    Create Account
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Stats bar under title (only for logged in users) */}
          {user && levelProgress && (
            <div className="max-w-4xl mx-auto mb-8 md:mb-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl shadow-lg shadow-primary-500/20 p-5 md:p-8 text-white">
              {/* Level and XP Progress */}
              <div className="mb-5 md:mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-xl md:text-2xl font-semibold">Level {levelProgress.currentLevel}</span>
                  </div>
                  <div className="text-sm opacity-90">
                    {levelProgress.xpInCurrentLevel.toLocaleString()} / {levelProgress.xpNeededInLevel.toLocaleString()} XP
                  </div>
                </div>
                {/* XP Progress Bar */}
                <div className="w-full bg-white/20 rounded-full h-3 md:h-4 overflow-hidden">
                  <div
                    className="bg-white h-full rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2"
                    style={{ width: `${levelProgress.percentageToNextLevel}%` }}
                  >
                    <span className="text-xs font-semibold text-primary-600">
                      {levelProgress.percentageToNextLevel}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 md:gap-6 text-center">
                <div>
                  <div className="text-2xl md:text-3xl font-semibold">{user.totalXP.toLocaleString()}</div>
                  <div className="text-sm opacity-90">Total XP</div>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-semibold">{user.studyStreak}</div>
                  <div className="text-sm opacity-90">Day Streak</div>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-semibold">{Math.floor(user.totalStudyTime / 60)}</div>
                  <div className="text-sm opacity-90">Minutes Studied</div>
                </div>
              </div>
            </div>
          )}

          {/* Action Cards with scroll animation */}
          <div
            ref={cardsAnimation.ref}
            className={`max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-16 ${cardsAnimation.animationClasses}`}
          >
            {/* Start Talking Card */}
            <button
              className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm hover:shadow-lg p-6 md:p-8 text-center transition-all duration-300 transform hover:-translate-y-1 border border-neutral-100 dark:border-neutral-700 hover:border-primary-200 dark:hover:border-primary-800 group"
              onClick={() => user ? handleNavigate('/conversation') : setShowLanguageModal(true)}
            >
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <svg className="w-7 h-7 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Start Talking</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Jump into a conversation now</p>
              {!user && <span className="inline-block mt-4 text-xs font-medium text-primary-600 bg-primary-50 dark:bg-primary-900/30 px-3 py-1.5 rounded-full">Try it free</span>}
            </button>

            {/* Flashcards Card */}
            <button
              className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm hover:shadow-lg p-6 md:p-8 text-center transition-all duration-300 transform hover:-translate-y-1 border border-neutral-100 dark:border-neutral-700 hover:border-accent-200 dark:hover:border-accent-800 group"
              onClick={() => handleNavigate('/flashcards', 'Flashcards')}
            >
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-accent-50 dark:bg-accent-900/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <svg className="w-7 h-7 text-accent-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Flashcards</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Review and manage your cards</p>
              {!user && <span className="inline-block mt-4 text-xs font-medium text-neutral-500 bg-neutral-100 dark:bg-neutral-700 px-3 py-1.5 rounded-full">Login required</span>}
            </button>

            {/* Study Plan Card (Premium) */}
            <button
              className="bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-2xl shadow-md shadow-primary-500/20 hover:shadow-lg hover:shadow-primary-500/30 p-6 md:p-8 text-center transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group"
              onClick={() => handleNavigate('/study-plan', 'Study Plan')}
            >
              <div className="absolute top-3 right-3 text-[10px] bg-warning-400 text-warning-900 px-2.5 py-1 rounded-full font-semibold uppercase tracking-wide">
                Premium
              </div>
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Study Plan</h3>
              <p className="text-sm opacity-90">Set and track your study goals</p>
              {!user && <span className="inline-block mt-4 text-xs font-medium text-white/80 bg-white/20 px-3 py-1.5 rounded-full">Login required</span>}
            </button>
          </div>

          {/* Selling Points Section (Only for guests) with scroll animation */}
          {!user && (
            <div
              ref={sellingPointsAnimation.ref}
              className={`max-w-4xl mx-auto py-16 border-t border-neutral-200 dark:border-neutral-800 ${sellingPointsAnimation.animationClasses}`}
            >
              <h2 className="text-3xl font-semibold text-center text-neutral-900 dark:text-neutral-100 mb-12">
                Why learn with Fluntli?
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
                {/* Talk Anytime */}
                <div className="flex gap-5 p-6 rounded-xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 hover:border-success-200 dark:hover:border-success-800 hover:shadow-md transition-all duration-200">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-success-50 dark:bg-success-900/30 flex items-center justify-center">
                    <svg className="w-7 h-7 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Talk Anytime</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">Get immediate responses and corrections 24/7. No scheduling required, just start talking whenever you have a free moment.</p>
                  </div>
                </div>

                {/* Smart Flashcards */}
                <div className="flex gap-5 p-6 rounded-xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 hover:border-accent-200 dark:hover:border-accent-800 hover:shadow-md transition-all duration-200">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-accent-50 dark:bg-accent-900/30 flex items-center justify-center">
                    <svg className="w-7 h-7 text-accent-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Smart Flashcards</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">Automatically generate flashcards for words you don't know during conversations. Review them later to reinforce your learning.</p>
                  </div>
                </div>

                {/* Learn Faster */}
                <div className="flex gap-5 p-6 rounded-xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 hover:border-primary-200 dark:hover:border-primary-800 hover:shadow-md transition-all duration-200">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
                    <svg className="w-7 h-7 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Learn Faster</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">Practice speaking without the fear of embarrassment. Build confidence and fluency at your own pace.</p>
                  </div>
                </div>

                {/* Premium Experience */}
                <div className="flex gap-5 p-6 rounded-xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 hover:border-warning-200 dark:hover:border-warning-800 hover:shadow-md transition-all duration-200">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-warning-50 dark:bg-warning-900/30 flex items-center justify-center">
                    <svg className="w-7 h-7 text-warning-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Premium Experience</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">Upgrade to unlock premium voices, unlimited conversations, and advanced study plans tailored to your goals.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Ad placement for free users - Uncomment when you have a real ad slot ID from Google AdSense */}
          {/* <div className="mt-8 md:mt-12">
            <AdSense
              slot="YOUR_REAL_AD_SLOT_ID_HERE"
              format="auto"
              responsive={true}
              className="text-center"
            />
          </div> */}
        </div>
      </div>
    </>
  )
}

export default Home
